import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Alert, CircularProgress } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Bell,
  Building2,
  Check,
  Clock3,
  ExternalLink,
  KeyRound,
  Link2,
  Loader2,
  Plug,
  Rocket,
  ShieldCheck,
  Sparkles,
  WalletCards,
} from "lucide-react";
import EntryInstructionEmailPreference from "../components/onboarding/EntryInstructionEmailPreference";
import MessagingSetupGuide from "../components/onboarding/MessagingSetupGuide";
import PmsConnectionPanel from "../components/onboarding/PmsConnectionPanel";
import {
  addProperty,
  getFlags,
  getHostProfile,
  getHostProperties,
  updateHostProperty,
} from "../store/thunks/hostActions";
import request from "../utils/request";
import {
  deriveOnboardingState,
  getNextIncompleteStep,
  getOnboardingMode,
  getStepCompletion,
  isPropertyAccessComplete,
  isPropertyBasicsComplete,
  ONBOARDING_STEP_IDS,
  ONBOARDING_STEPS,
  readOnboardingProgress,
  writeOnboardingProgress,
} from "../utils/onboarding";
import {
  trackHostOnboardingActivationCompleted,
  trackHostOnboardingMessagingViewed,
  trackHostOnboardingOperationalDetailsCompleted,
  trackHostOnboardingPmsConnected,
  trackHostOnboardingPmsSelected,
  trackHostOnboardingPropertyBasicsCompleted,
  trackHostOnboardingPropertyCreated,
  trackHostOnboardingProviderSelected,
  trackHostOnboardingResumeResolved,
  trackHostOnboardingResumed,
  trackHostOnboardingSetupModeSelected,
  trackHostOnboardingStarted,
  trackHostOnboardingStripeCompleted,
  trackHostOnboardingStripeSkipped,
  trackHostOnboardingSupportReentry,
} from "../utils/hostAnalytics";

const STEP_QUERY_VALUES = new Set([
  ...ONBOARDING_STEPS.map((step) => step.id),
  ONBOARDING_STEP_IDS.SUCCESS,
  "property",
]);

const defaultAddress = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  zipCode: "",
};

const pmsProviderOptions = [
  { value: "guesty", label: "Guesty", copy: "Connect with an API token and sync properties." },
  { value: "streamline", label: "Streamline", copy: "Connect API credentials and sync inventory." },
  { value: "hospitable", label: "Hospitable", copy: "Connect through secure OAuth." },
  { value: "other", label: "Other PMS", copy: "Use manual setup and get help from Support if needed." },
];

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 42 : -42,
    opacity: 0,
    filter: "blur(2px)",
  }),
  center: {
    x: 0,
    opacity: 1,
    filter: "blur(0px)",
  },
  exit: (direction) => ({
    x: direction > 0 ? -42 : 42,
    opacity: 0,
    filter: "blur(2px)",
  }),
};

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function buildPropertyForm(property) {
  return {
    address: property?.address ? { ...defaultAddress, ...property.address } : { ...defaultAddress },
    listingTitle: property?.listingTitle || "",
    description: property?.description || "",
    keyInfo: property?.keyInfo || "",
    checkInTime: parseDate(property?.checkInTime),
    checkOutTime: parseDate(property?.checkOutTime),
    hideAddress: Boolean(property?.hideAddress),
  };
}

function formToProperty(form) {
  return {
    listingTitle: form.listingTitle,
    address: form.address,
    checkInTime: form.checkInTime,
    checkOutTime: form.checkOutTime,
    description: form.description,
    keyInfo: form.keyInfo,
    hideAddress: form.hideAddress,
  };
}

function normalizeStep(value) {
  const step = String(value || "").trim().toLowerCase();
  if (step === "property") return ONBOARDING_STEP_IDS.PROPERTY_BASICS;
  return STEP_QUERY_VALUES.has(step) ? step : "";
}

function getStepIndex(stepId) {
  if (stepId === ONBOARDING_STEP_IDS.SUCCESS) return ONBOARDING_STEPS.length;
  return ONBOARDING_STEPS.findIndex((step) => step.id === stepId);
}

function hasRequiredBasics(form) {
  return isPropertyBasicsComplete(formToProperty(form));
}

function hasRequiredAccess(form) {
  return isPropertyAccessComplete(formToProperty(form));
}

function getTutorialBlockers(state) {
  const blockers = [];
  if (!state.completion.mode) blockers.push("Choose a setup style.");
  if (!state.completion.pmsDecision) blockers.push("Choose how properties come into Bellhopt.");
  if (state.usesPms && !state.completion.pmsConnected) blockers.push("Connect your supported PMS or continue manually.");
  return blockers;
}

function getPendingItems(state) {
  const items = [];
  if (!state.completion.firstProperty || !state.completion.propertyBasics) {
    items.push({ label: "Add a property", description: "Title, address, and stay timing.", step: ONBOARDING_STEP_IDS.PROPERTY_BASICS });
  }
  if (!state.completion.entryAccess && state.completion.firstProperty) {
    items.push({ label: "Add entry instructions", description: "Lockbox, key, and access details.", step: ONBOARDING_STEP_IDS.PROPERTY_ACCESS });
  }
  if (!state.completion.stripe) {
    items.push({ label: "Connect Stripe", description: "Required for receiving payouts.", step: ONBOARDING_STEP_IDS.STRIPE });
  }
  if (!state.completion.messaging) {
    items.push({ label: "Set up guest messaging", description: "Drives guest discovery and orders.", step: ONBOARDING_STEP_IDS.MESSAGING });
  }
  return items;
}

function StepFrame({ eyebrow, title, copy, children, actions, footerNote }) {
  return (
    <div className="rounded-2xl border border-[#e8e8e8] bg-white">
      <div className="px-5 pb-5 pt-5 md:px-7 md:pb-6 md:pt-6">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[1.5px] text-[#999999]">{eyebrow}</p>
          <h1 className="mt-1.5 text-[22px] font-bold tracking-[-0.3px] text-[#1a1a1a]">{title}</h1>
          {copy && <p className="mt-1.5 max-w-2xl text-sm font-normal leading-relaxed text-[#666666]">{copy}</p>}
        </motion.div>

        <div className="mt-5">{children}</div>

        {(footerNote || actions) && (
          <footer className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#e8e8e8] pt-4">
            <div className="text-[11px] font-bold uppercase tracking-[1.5px] text-[#999999]">
              {footerNote}
            </div>
            <div className="flex flex-wrap items-center gap-3">{actions}</div>
          </footer>
        )}
      </div>
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled, loading, icon: Icon = ArrowRight, type = "button", tone = "dark" }) {
  const toneClass =
    tone === "light"
      ? "bg-white text-[#1a1a1a] hover:bg-gray-100"
      : "bg-[#1a1a1a] text-white hover:bg-[#333333]";
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition disabled:cursor-not-allowed disabled:opacity-50 ${toneClass}`}
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : Icon ? <Icon size={16} /> : null}
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, disabled, icon: Icon, type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center gap-2 rounded-xl border-[1.5px] border-[#e0e0e0] bg-white px-4 py-2.5 text-sm font-semibold text-[#999999] transition hover:border-[#d4d4d4] hover:text-[#666666] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

function TextInput({ label, value, onChange, placeholder, autoComplete }) {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[1.5px] text-[#999999]">{label}</label>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full rounded-lg border border-[#ddd] bg-[#f7f7f7] px-4 py-3 text-[15px] font-normal text-[#1a1a1a] outline-none transition placeholder:text-[#999999] focus:border-[#4a90d9] focus:ring-2 focus:ring-[#4a90d9]/10"
      />
    </div>
  );
}

function TimeInput({ label, value, onChange }) {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[1.5px] text-[#999999]">{label}</label>
      <DatePicker
        selected={value}
        onChange={onChange}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={15}
        timeCaption="Time"
        dateFormat="h:mm aa"
        className="w-full rounded-lg border border-[#ddd] bg-[#f7f7f7] px-4 py-3 text-[15px] font-normal text-[#1a1a1a] outline-none transition focus:border-[#4a90d9] focus:ring-2 focus:ring-[#4a90d9]/10"
      />
    </div>
  );
}

function OptionTile({ icon: Icon, title, copy, selected, onClick, children }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.985 }}
      className={`group relative overflow-hidden rounded-2xl border p-5 text-left transition ${
        selected
          ? "border-[#1a1a1a] bg-[#1a1a1a] text-white shadow-lg"
          : "border-[#e8e8e8] bg-[#f5f5f5] text-[#1a1a1a] hover:border-[#d4d4d4] hover:shadow-md"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-xl transition ${
            selected ? "bg-white/15 text-white" : "bg-white text-[#1a1a1a]"
          }`}
        >
          <Icon size={20} />
        </div>
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full border transition ${
            selected ? "border-white/30 bg-white text-[#1a1a1a]" : "border-[#e8e8e8] bg-white text-[#d4d4d4]"
          }`}
        >
          {selected && <Check size={14} />}
        </span>
      </div>
      <h2 className="mt-4 text-lg font-bold tracking-[-0.3px]">{title}</h2>
      <p className={`mt-1.5 text-sm font-normal leading-5 ${selected ? "text-white/80" : "text-[#666666]"}`}>{copy}</p>
      {children}
    </motion.button>
  );
}

function OnboardingStage({
  state,
  activeStep,
  direction,
  children,
  onBack,
  source,
}) {
  const activeIndex = getStepIndex(activeStep);
  const visibleIndex = activeStep === ONBOARDING_STEP_IDS.SUCCESS ? ONBOARDING_STEPS.length : Math.max(activeIndex + 1, 1);
  const totalSteps = ONBOARDING_STEPS.length;
  const progress = Math.min(100, Math.max(8, (visibleIndex / totalSteps) * 100));
  const activeMeta =
    ONBOARDING_STEPS.find((step) => step.id === activeStep) ||
    { label: "Success", shortLabel: "Done", required: false };
  const isFirstStep = activeStep === ONBOARDING_STEP_IDS.MODE;
  return (
    <div className="relative min-h-full bg-white">
      <div className="relative mx-auto flex max-w-4xl flex-col px-4 py-3 md:px-6">
        <header className="py-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {!isFirstStep && (
                <button
                  type="button"
                  onClick={onBack}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#e8e8e8] bg-[#f5f5f5] text-[#666666] transition hover:text-[#1a1a1a]"
                  aria-label="Previous setup step"
                >
                  <ArrowLeft size={16} />
                </button>
              )}
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[1.5px] text-[#999999]">
                  Step {Math.min(visibleIndex, totalSteps)} of {totalSteps}
                </p>
                <h2 className="truncate text-sm font-bold text-[#1a1a1a]">{activeMeta.label}</h2>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {!state.completion.stripe && (
                <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-bold text-amber-700">
                  Payouts pending
                </span>
              )}
              {source === "support" && (
                <span className="rounded-full bg-primary-50 px-2.5 py-0.5 text-[11px] font-bold text-primary-600">
                  Support entry
                </span>
              )}
            </div>
          </div>

          <div className="mt-2.5 h-1 overflow-hidden rounded-full bg-[#f5f5f5]">
            <motion.div
              className="h-full rounded-full bg-[#1a1a1a]"
              animate={{ width: `${progress}%` }}
              transition={{ type: "spring", stiffness: 160, damping: 24 }}
            />
          </div>

          <div className="mt-2 hidden items-center justify-between gap-1.5 md:flex">
            {ONBOARDING_STEPS.map((step) => {
              const complete = getStepCompletion(state, step.id);
              const current = step.id === activeStep;
              return (
                <div key={step.id} className="flex min-w-0 items-center gap-1.5">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      complete ? "bg-green-500" : current ? "bg-[#1a1a1a]" : "bg-[#e8e8e8]"
                    }`}
                  />
                  <span
                    className={`truncate text-[10px] font-semibold ${
                      current ? "text-[#1a1a1a]" : complete ? "text-[#666666]" : "text-[#999999]"
                    }`}
                  >
                    {step.shortLabel}
                  </span>
                </div>
              );
            })}
          </div>
        </header>

        <main className="relative mt-3 overflow-visible">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={activeStep}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: "spring", stiffness: 220, damping: 28, mass: 0.8 }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

function ModeStep({ setupMode, onSelect }) {
  return (
    <StepFrame
      eyebrow="Bellhopt host setup"
      title="Choose your setup pace."
      copy="Pick the path that fits how you work. Both reach the same destination."
      footerNote="Selection advances automatically"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <OptionTile
          icon={Rocket}
          title="Fast setup"
          copy="Direct path for hosts who already know their tools."
          selected={setupMode === "fast"}
          onClick={() => onSelect("fast")}
        />
        <OptionTile
          icon={Sparkles}
          title="Guided setup"
          copy="Adds context around operations, access, and messaging."
          selected={setupMode === "guided"}
          onClick={() => onSelect("guided")}
        />
      </div>
    </StepFrame>
  );
}

function StripeStep({ complete, skipped, loading, error, onConnect, onSkip, onContinue }) {
  return (
    <StepFrame
      eyebrow="Payout readiness"
      title={complete ? "Payouts connected." : "Connect payouts or set up later."}
      copy="Stripe is required for payouts but not for completing setup."
      actions={
        complete ? (
          <PrimaryButton onClick={onContinue}>Continue</PrimaryButton>
        ) : (
          <>
            <SecondaryButton onClick={onSkip}>Set up later</SecondaryButton>
            <PrimaryButton onClick={onConnect} loading={loading} icon={ExternalLink}>
              Connect Stripe
            </PrimaryButton>
          </>
        )
      }
      footerNote="Optional for setup"
    >
      {error && <Alert severity="error" className="mb-4">{error}</Alert>}
      <div className="flex items-start gap-4 rounded-2xl border border-[#e8e8e8] bg-[#f5f5f5] p-5">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#1a1a1a] text-white">
          {complete ? <ShieldCheck size={20} /> : <WalletCards size={20} />}
        </div>
        <div>
          <h2 className="text-base font-bold text-[#1a1a1a]">
            {complete ? "Ready to receive host payouts." : "Keep setting up while payouts wait."}
          </h2>
          <p className="mt-1 text-sm font-normal leading-relaxed text-[#666666]">
            {complete ? "Stripe is linked and payouts are active." : "Connecting opens Stripe's secure flow. Skipping preserves your setup progress."}
          </p>
        </div>
      </div>
    </StepFrame>
  );
}

function PmsPathStep({ pmsDecision, onDecision }) {
  return (
    <StepFrame
      eyebrow="Property source"
      title="How do your properties sync?"
      copy="Bellhopt can connect to a PMS or work with manually managed properties."
      footerNote="Selection advances automatically"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <OptionTile
          icon={Plug}
          title="I use a PMS"
          copy="Connect a supported provider or continue manually."
          selected={pmsDecision === "yes"}
          onClick={() => onDecision("yes")}
        />
        <OptionTile
          icon={KeyRound}
          title="No PMS"
          copy="Add properties manually and keep things lightweight."
          selected={pmsDecision === "no"}
          onClick={() => onDecision("no")}
        />
      </div>
    </StepFrame>
  );
}

function PmsProviderStep({ state, hostInfo, onProviderSelect, onProviderConnected, onContinue, onManualFallback }) {
  const providerSelected = Boolean(state.selectedPms);
  const selectedUnsupported = providerSelected && state.selectedPms === "other";
  const canContinue = state.completion.pms || selectedUnsupported;

  return (
    <StepFrame
      eyebrow="PMS connection"
      title={providerSelected ? `Connect ${pmsProviderOptions.find((option) => option.value === state.selectedPms)?.label || "your PMS"}.` : "Choose your provider."}
      copy="Bellhopt supports Guesty, Streamline, and Hospitable. Other PMS tools use manual setup."
      actions={
        <>
          {state.requiresPmsConnection && !state.completion.pmsConnected && (
            <SecondaryButton onClick={onManualFallback}>Continue manually</SecondaryButton>
          )}
          <PrimaryButton onClick={onContinue} disabled={!canContinue}>
            Continue
          </PrimaryButton>
        </>
      }
      footerNote="Provider-aware setup"
    >
      <div className="flex min-h-0 flex-col gap-4">
        <div className="grid gap-3 md:grid-cols-4">
          {pmsProviderOptions.map((provider) => (
            <button
              key={provider.value}
              type="button"
              onClick={() => onProviderSelect(provider.value)}
              className={`rounded-2xl border p-4 text-left transition ${
                state.selectedPms === provider.value
                  ? "border-[#1a1a1a] bg-[#1a1a1a] text-white shadow-md"
                  : "border-[#e8e8e8] bg-[#f5f5f5] text-[#1a1a1a] hover:border-[#d4d4d4] hover:shadow-sm"
              }`}
            >
              <p className="text-sm font-bold">{provider.label}</p>
              <p className={`mt-1.5 text-xs font-normal leading-5 ${state.selectedPms === provider.value ? "text-white/80" : "text-[#666666]"}`}>
                {provider.copy}
              </p>
            </button>
          ))}
        </div>

        <div className="flex-1 border-t border-[#e8e8e8] pt-4">
          {!providerSelected ? (
            <div className="flex min-h-[140px] items-center justify-center text-center">
              <div>
                <Plug className="mx-auto text-[#d4d4d4]" size={32} />
                <p className="mt-2 text-sm font-semibold text-[#999999]">Select a provider to continue.</p>
              </div>
            </div>
          ) : selectedUnsupported ? (
            <div className="flex min-h-[140px] flex-col justify-center rounded-xl border border-[#e8e8e8] bg-[#f5f5f5] p-5">
              <h2 className="text-base font-bold text-[#1a1a1a]">Manual setup is the right path.</h2>
              <p className="mt-1.5 max-w-2xl text-sm font-normal leading-relaxed text-[#666666]">
                Add the property manually, then use Support for messaging or link distribution.
              </p>
            </div>
          ) : (
            <PmsConnectionPanel
              hostInfo={hostInfo}
              providerOverride={state.selectedPms}
              source="onboarding"
              onConnected={onProviderConnected}
            />
          )}
        </div>
      </div>
    </StepFrame>
  );
}

function PropertyBasicsStep({ state, form, setForm, onContinue, onSkip }) {
  const canContinue = hasRequiredBasics(form);
  const updateAddress = (field, value) => {
    setForm((current) => ({
      ...current,
      address: { ...current.address, [field]: value },
    }));
  };

  return (
    <StepFrame
      eyebrow={state.firstActiveProperty ? "Property basics" : "First property"}
      title={state.firstActiveProperty ? "Confirm the property." : "Add your first property."}
      copy="Title, address, and stay timing. You can skip this and add a property later."
      actions={
        <>
          <SecondaryButton onClick={onSkip}>Skip for now</SecondaryButton>
          <PrimaryButton onClick={onContinue} disabled={!canContinue}>
            Continue to access
          </PrimaryButton>
        </>
      }
      footerNote="Property setup is optional"
    >
      <div className="grid gap-4">
        <div className="grid gap-3 md:grid-cols-[1fr_0.9fr]">
          <TextInput
            label="Listing title"
            value={form.listingTitle}
            onChange={(value) => setForm((current) => ({ ...current, listingTitle: value }))}
            placeholder="Seaside Villa"
          />
          <div className="grid grid-cols-2 gap-3">
            <TimeInput
              label="Check-in"
              value={form.checkInTime}
              onChange={(value) => setForm((current) => ({ ...current, checkInTime: value }))}
            />
            <TimeInput
              label="Check-out"
              value={form.checkOutTime}
              onChange={(value) => setForm((current) => ({ ...current, checkOutTime: value }))}
            />
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <TextInput
            label="Address line 1"
            value={form.address.line1}
            onChange={(value) => updateAddress("line1", value)}
            placeholder="123 Ocean Ave"
            autoComplete="address-line1"
          />
          <TextInput
            label="Address line 2"
            value={form.address.line2}
            onChange={(value) => updateAddress("line2", value)}
            placeholder="Unit, building, floor"
            autoComplete="address-line2"
          />
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <TextInput
            label="City"
            value={form.address.city}
            onChange={(value) => updateAddress("city", value)}
            autoComplete="address-level2"
          />
          <TextInput
            label="State"
            value={form.address.state}
            onChange={(value) => updateAddress("state", value)}
            autoComplete="address-level1"
          />
          <TextInput
            label="Zip code"
            value={form.address.zipCode}
            onChange={(value) => updateAddress("zipCode", value)}
            autoComplete="postal-code"
          />
        </div>
      </div>
    </StepFrame>
  );
}

function PropertyAccessStep({ state, form, setForm, saving, error, onSave, onSkip }) {
  const canSave = hasRequiredBasics(form) && hasRequiredAccess(form);
  const hasProperty = Boolean(state.firstActiveProperty?._id);

  return (
    <StepFrame
      eyebrow="Entry access"
      title="Make access unambiguous."
      copy="Include lockbox, key, gate, parking, and delivery instructions."
      actions={
        <>
          <SecondaryButton onClick={onSkip}>Skip for now</SecondaryButton>
          <PrimaryButton onClick={onSave} disabled={!canSave} loading={saving} icon={Check}>
            {hasProperty ? "Save and continue" : "Create and continue"}
          </PrimaryButton>
        </>
      }
      footerNote={hasProperty ? "Saves to existing property" : "Creates property when saved"}
    >
      {error && <Alert severity="warning" className="mb-4">{error}</Alert>}

      <div className="grid gap-4">
        <div>
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[1.5px] text-[#999999]">Entry instructions</label>
          <textarea
            rows={6}
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            placeholder="Example: Lockbox is mounted left of the garage door. Code is 1234. Park in the driveway, use the side gate, and return the key to the lockbox after delivery."
            className="h-[180px] w-full resize-none rounded-lg border border-[#ddd] bg-[#f7f7f7] px-4 py-3 text-[15px] font-normal leading-6 text-[#1a1a1a] outline-none transition placeholder:text-[#999999] focus:border-[#4a90d9] focus:ring-2 focus:ring-[#4a90d9]/10"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
          <TextInput
            label="Vendor or access code"
            value={form.keyInfo}
            onChange={(value) => setForm((current) => ({ ...current, keyInfo: value }))}
            placeholder="Gate code, vendor code, or lockbox note"
          />
          <label className="flex items-center gap-3 rounded-lg border border-[#ddd] bg-[#f7f7f7] px-4 py-3 text-sm font-semibold text-[#1a1a1a]">
            <input
              type="checkbox"
              checked={form.hideAddress}
              onChange={(event) => setForm((current) => ({ ...current, hideAddress: event.target.checked }))}
              className="h-4 w-4 rounded border-[#ddd] text-[#4a90d9] focus:ring-[#4a90d9]"
            />
            Hide address from guests
          </label>
        </div>
        <div className="flex items-start gap-3 rounded-xl border border-[#e8e8e8] bg-[#f5f5f5] px-4 py-3 text-sm font-normal leading-relaxed text-[#666666]">
          <Clock3 size={16} className="mt-0.5 flex-shrink-0 text-[#999999]" />
          Deliveries are coordinated using check-in, check-out, and guest arrival context.
        </div>
      </div>
    </StepFrame>
  );
}

function NotificationsStep({ hostId, state, onConfigured, onSkip, onContinue }) {
  const ready = state.completion.notificationPreference;

  return (
    <StepFrame
      eyebrow="Delivery alerts"
      title="Set your delivery alert preference."
      copy="Get email reminders when Bellhopt needs access confirmation or timing context."
      actions={
        <>
          {!ready && <SecondaryButton onClick={onSkip}>Continue without reminders</SecondaryButton>}
          <PrimaryButton onClick={onContinue} disabled={!ready}>
            Continue
          </PrimaryButton>
        </>
      }
      footerNote={ready ? "Preference set" : "Choose or acknowledge"}
    >
      <div className="flex items-start gap-4 rounded-2xl border border-[#e8e8e8] bg-[#f5f5f5] p-5">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#1a1a1a] text-white">
          <Bell size={20} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-[#1a1a1a]">Email me before every delivery</p>
          <p className="mt-0.5 text-sm font-normal text-[#666666]">Recommended when entry details may need confirmation.</p>
        </div>
      </div>
      <div className="mt-4">
        <EntryInstructionEmailPreference
          hostId={hostId}
          title="Email me before every delivery"
          description="Turn this on if you want Bellhopt to email you before deliveries that need entry details or timing confirmation."
          onConfigured={onConfigured}
          className="!rounded-none !border-0 !bg-transparent !p-0"
        />
      </div>
    </StepFrame>
  );
}

function MessagingStep({ state, hostId, onContinue, onSkip }) {
  return (
    <StepFrame
      eyebrow="Guest messaging"
      title="Put Bellhopt where guests look."
      copy="Copy property URLs into your automated guest messages for easy discovery."
      actions={
        <>
          <SecondaryButton onClick={onSkip}>Add messaging later</SecondaryButton>
          <PrimaryButton onClick={onContinue}>Continue to activation</PrimaryButton>
        </>
      }
      footerNote="Recommended, not required"
    >
      <div className="min-h-0">
        <MessagingSetupGuide
          hostId={hostId}
          properties={state.activeProperties}
          selectedPms={state.selectedPms}
          source="onboarding"
          stage
        />
      </div>
    </StepFrame>
  );
}

function ActivationStep({ state, onActivate, onGoToStep }) {
  const blockers = getTutorialBlockers(state);
  const pending = getPendingItems(state);

  return (
    <StepFrame
      eyebrow="Activation"
      title={blockers.length > 0 ? "Complete required steps first." : "Complete the setup tutorial."}
      copy={blockers.length > 0 ? undefined : "You can activate now and finish remaining items from the dashboard."}
      actions={
        blockers.length > 0 ? (
          <PrimaryButton onClick={() => onGoToStep(getNextIncompleteStep(state))} icon={ArrowRight}>
            Continue setup
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={onActivate} icon={Check}>
            Activate Bellhopt
          </PrimaryButton>
        )
      }
      footerNote={blockers.length > 0 ? "Required steps remain" : "Pending items can be completed later"}
    >
      {blockers.length > 0 ? (
        <div className="divide-y divide-[#e8e8e8] rounded-xl border border-[#e8e8e8] bg-[#f5f5f5]">
          {blockers.map((blocker) => (
            <div key={blocker} className="px-4 py-3 text-sm font-semibold text-amber-800">
              {blocker}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-xl border border-[#e8e8e8] bg-[#f5f5f5] p-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#1a1a1a] text-white">
              <BadgeCheck size={20} />
            </div>
            <div>
              <p className="text-sm font-bold text-[#1a1a1a]">Tutorial steps are complete.</p>
              <p className="text-sm font-normal text-[#666666]">Activate to finish the setup flow.</p>
            </div>
          </div>

          {pending.length > 0 && (
            <div>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[1.5px] text-[#999999]">Still pending</p>
              <div className="divide-y divide-[#e8e8e8] rounded-xl border border-[#e8e8e8]">
                {pending.map((item) => (
                  <button
                    key={item.step}
                    type="button"
                    onClick={() => onGoToStep(item.step)}
                    className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-[#f5f5f5]"
                  >
                    <span>
                      <span className="block text-sm font-bold text-[#1a1a1a]">{item.label}</span>
                      <span className="block text-sm font-normal text-[#666666]">{item.description}</span>
                    </span>
                    <ArrowRight size={16} className="flex-shrink-0 text-[#d4d4d4]" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </StepFrame>
  );
}

function SuccessStep({ state, onGoToStep }) {
  const firstProperty = state.activeProperties[0];
  const pending = getPendingItems(state);

  return (
    <StepFrame
      eyebrow="Setup complete"
      title="Tutorial activated."
      copy={pending.length > 0
        ? "The tutorial is done. Complete the remaining items from your dashboard when ready."
        : "Everything looks good. Manage your setup from the dashboard."
      }
      footerNote="Next steps"
    >
      <div className="space-y-4">
        {pending.length > 0 && (
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[1.5px] text-[#999999]">Still pending</p>
            <div className="divide-y divide-[#e8e8e8] rounded-xl border border-[#e8e8e8]">
              {pending.map((item) => (
                <button
                  key={item.step}
                  type="button"
                  onClick={() => onGoToStep(item.step)}
                  className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-[#f5f5f5]"
                >
                  <span>
                    <span className="block text-sm font-bold text-[#1a1a1a]">{item.label}</span>
                    <span className="block text-sm font-normal text-[#666666]">{item.description}</span>
                  </span>
                  <ArrowRight size={16} className="flex-shrink-0 text-[#d4d4d4]" />
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[1.5px] text-[#999999]">Quick actions</p>
          <div className="divide-y divide-[#e8e8e8] rounded-xl border border-[#e8e8e8]">
            {firstProperty?.uniqueLink && (
              <a
                href={firstProperty.uniqueLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-[#f5f5f5]"
              >
                <span className="flex items-center gap-3">
                  <Link2 size={16} className="text-[#999999]" />
                  <span className="text-sm font-bold text-[#1a1a1a]">View guest experience</span>
                </span>
                <ArrowRight size={16} className="text-[#d4d4d4]" />
              </a>
            )}
            <Link
              to="/host"
              className="flex items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-[#f5f5f5]"
            >
              <span className="flex items-center gap-3">
                <Building2 size={16} className="text-[#999999]" />
                <span className="text-sm font-bold text-[#1a1a1a]">Go to dashboard</span>
              </span>
              <ArrowRight size={16} className="text-[#d4d4d4]" />
            </Link>
            <Link
              to="/support"
              className="flex items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-[#f5f5f5]"
            >
              <span className="flex items-center gap-3">
                <Sparkles size={16} className="text-[#999999]" />
                <span className="text-sm font-bold text-[#1a1a1a]">Revisit setup help</span>
              </span>
              <ArrowRight size={16} className="text-[#d4d4d4]" />
            </Link>
          </div>
        </div>
      </div>
    </StepFrame>
  );
}

function RefresherSummary({ state, onGoToStep }) {
  return (
    <div className="relative min-h-full bg-white">
      <div className="relative mx-auto flex max-w-4xl flex-col px-4 py-6 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[1.5px] text-[#999999]">
            Your setup
          </p>
          <h1 className="mt-1.5 text-[22px] font-bold tracking-[-0.3px] text-[#1a1a1a]">
            Review your Bellhopt configuration
          </h1>
          <p className="mt-1.5 max-w-2xl text-sm font-normal leading-relaxed text-[#666666]">
            Everything is active. Revisit any step for a refresher or to update your settings.
          </p>
        </motion.div>

        <div className="mt-6 divide-y divide-[#e8e8e8] rounded-2xl border border-[#e8e8e8]">
          {ONBOARDING_STEPS.map((step) => {
            const complete = getStepCompletion(state, step.id);
            return (
              <button
                key={step.id}
                type="button"
                onClick={() => onGoToStep(step.id)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-[#f5f5f5]"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full ${
                      complete
                        ? "bg-green-500 text-white"
                        : "border border-[#e8e8e8] bg-[#f5f5f5] text-[#d4d4d4]"
                    }`}
                  >
                    {complete && <Check size={14} />}
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-[#1a1a1a]">{step.label}</span>
                    <span className="block text-xs font-normal text-[#999999]">
                      {complete ? "Completed" : step.required ? "Required" : "Optional"}
                    </span>
                  </span>
                </div>
                <ArrowRight size={16} className="flex-shrink-0 text-[#d4d4d4]" />
              </button>
            );
          })}
        </div>

        <div className="mt-5">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[1.5px] text-[#999999]">Quick actions</p>
          <div className="divide-y divide-[#e8e8e8] rounded-2xl border border-[#e8e8e8]">
            <Link
              to="/host"
              className="flex items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-[#f5f5f5]"
            >
              <span className="flex items-center gap-3">
                <Building2 size={16} className="text-[#999999]" />
                <span className="text-sm font-bold text-[#1a1a1a]">Go to dashboard</span>
              </span>
              <ArrowRight size={16} className="text-[#d4d4d4]" />
            </Link>
            <Link
              to="/support"
              className="flex items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-[#f5f5f5]"
            >
              <span className="flex items-center gap-3">
                <Sparkles size={16} className="text-[#999999]" />
                <span className="text-sm font-bold text-[#1a1a1a]">Support</span>
              </span>
              <ArrowRight size={16} className="text-[#d4d4d4]" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function HostOnboardingScreen() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hostInfo } = useSelector((state) => state.host);
  const { Info: hostDetails, loading: loadingHostDetails } = useSelector((state) => state.hostDetails);
  const { properties, loading: loadingProperties } = useSelector((state) => state.hostProperties);
  const hostFlags = useSelector((state) => state.hostFlags);

  const [localProgress, setLocalProgress] = useState({});
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [activeStep, setActiveStep] = useState(ONBOARDING_STEP_IDS.MODE);
  const [direction, setDirection] = useState(1);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState("");
  const [propertyForm, setPropertyForm] = useState(buildPropertyForm());
  const [propertySaving, setPropertySaving] = useState(false);
  const [propertyError, setPropertyError] = useState("");

  const initializedRef = useRef(false);
  const analyticsRef = useRef({
    started: false,
    stripe: false,
    pmsConnected: "",
    support: false,
    resumed: false,
    resumeResolved: false,
    messaging: false,
  });

  const profileForState = hostDetails || hostInfo;
  const onboardingState = useMemo(
    () =>
      deriveOnboardingState({
        hostInfo,
        hostDetails: profileForState,
        properties,
        hostFlags,
        localProgress,
      }),
    [hostInfo, profileForState, properties, hostFlags, localProgress]
  );

  const source = searchParams.get("source") || "dashboard";

  const deriveStateWithProgress = useCallback(
    (progress) =>
      deriveOnboardingState({
        hostInfo,
        hostDetails: profileForState,
        properties,
        hostFlags,
        localProgress: progress,
      }),
    [hostInfo, profileForState, properties, hostFlags]
  );

  const setStepInUrl = useCallback(
    (step) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("step", step);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const goToStep = useCallback(
    (step) => {
      const normalized = normalizeStep(step) || ONBOARDING_STEP_IDS.MODE;
      const currentIndex = getStepIndex(activeStep);
      const nextIndex = getStepIndex(normalized);
      setDirection(nextIndex >= currentIndex ? 1 : -1);
      setActiveStep(normalized);
      setStepInUrl(normalized);
    },
    [activeStep, setStepInUrl]
  );

  const goToNextFromState = useCallback(
    (state) => {
      const next = getNextIncompleteStep(state);
      goToStep(next === ONBOARDING_STEP_IDS.SUCCESS ? ONBOARDING_STEP_IDS.ACTIVATION : next);
    },
    [goToStep]
  );

  const persistProgress = useCallback(
    (patch) => {
      if (!hostInfo?._id) return localProgress;
      const next = writeOnboardingProgress(hostInfo._id, patch);
      setLocalProgress(next);
      return next;
    },
    [hostInfo?._id, localProgress]
  );

  const persistAndAdvance = useCallback(
    (patch) => {
      const nextProgress = persistProgress(patch);
      goToNextFromState(deriveStateWithProgress(nextProgress));
    },
    [deriveStateWithProgress, goToNextFromState, persistProgress]
  );

  useEffect(() => {
    if (!hostInfo?._id) return;
    dispatch(getHostProfile(hostInfo._id));
    dispatch(getHostProperties(hostInfo._id));
    dispatch(getFlags(hostInfo._id));
  }, [dispatch, hostInfo?._id]);

  useEffect(() => {
    if (!hostInfo?._id) return;
    setLocalProgress(readOnboardingProgress(hostInfo._id));
    setProgressLoaded(true);
    initializedRef.current = false;
  }, [hostInfo?._id]);

  useEffect(() => {
    if (!progressLoaded || !hostInfo?._id || initializedRef.current) return;
    const queryStep = normalizeStep(searchParams.get("step"));
    const nextStep = queryStep || getNextIncompleteStep(onboardingState);
    setActiveStep(nextStep);
    setStepInUrl(nextStep);
    initializedRef.current = true;
  }, [progressLoaded, hostInfo?._id, onboardingState, searchParams, setStepInUrl]);

  useEffect(() => {
    setPropertyForm(buildPropertyForm(onboardingState.firstActiveProperty));
  }, [onboardingState.firstActiveProperty]);

  useEffect(() => {
    if (!hostInfo?._id || !progressLoaded) return;
    if (!analyticsRef.current.started) {
      trackHostOnboardingStarted({ hostId: hostInfo._id, source });
      analyticsRef.current.started = true;
    }
    if (source === "support" && !analyticsRef.current.support) {
      trackHostOnboardingSupportReentry({ hostId: hostInfo._id, step: activeStep });
      analyticsRef.current.support = true;
    }
  }, [hostInfo?._id, progressLoaded, source, activeStep]);

  useEffect(() => {
    if (!hostInfo?._id || !progressLoaded || analyticsRef.current.resumeResolved) return;
    const nextStep = getNextIncompleteStep(onboardingState);
    trackHostOnboardingResumeResolved({ hostId: hostInfo._id, step: nextStep, source });
    analyticsRef.current.resumeResolved = true;
  }, [hostInfo?._id, progressLoaded, onboardingState, source]);

  useEffect(() => {
    if (!hostInfo?._id || !progressLoaded || analyticsRef.current.resumed) return;
    const nextStep = getNextIncompleteStep(onboardingState);
    if (nextStep !== ONBOARDING_STEP_IDS.MODE && nextStep !== ONBOARDING_STEP_IDS.SUCCESS) {
      trackHostOnboardingResumed({ hostId: hostInfo._id, step: nextStep });
      analyticsRef.current.resumed = true;
    }
  }, [hostInfo?._id, progressLoaded, onboardingState]);

  useEffect(() => {
    if (!hostInfo?._id) return;
    if (onboardingState.completion.stripe && !analyticsRef.current.stripe) {
      trackHostOnboardingStripeCompleted({ hostId: hostInfo._id });
      analyticsRef.current.stripe = true;
    }
    if (
      onboardingState.completion.pmsConnected &&
      onboardingState.selectedPms &&
      onboardingState.usesPms &&
      analyticsRef.current.pmsConnected !== onboardingState.selectedPms
    ) {
      trackHostOnboardingPmsConnected({ hostId: hostInfo._id, provider: onboardingState.selectedPms });
      analyticsRef.current.pmsConnected = onboardingState.selectedPms;
    }
  }, [
    hostInfo?._id,
    onboardingState.completion.stripe,
    onboardingState.completion.pmsConnected,
    onboardingState.selectedPms,
    onboardingState.usesPms,
  ]);

  useEffect(() => {
    if (activeStep !== ONBOARDING_STEP_IDS.MESSAGING || !hostInfo?._id) return;
    if (!localProgress.messagingViewed) {
      persistProgress({ messagingViewed: true, messagingViewedAt: new Date().toISOString() });
    }
    if (!analyticsRef.current.messaging) {
      trackHostOnboardingMessagingViewed({ hostId: hostInfo._id, provider: onboardingState.selectedPms });
      analyticsRef.current.messaging = true;
    }
  }, [activeStep, hostInfo?._id, localProgress.messagingViewed, onboardingState.selectedPms, persistProgress]);

  const goToPreviousStep = () => {
    const currentIndex = getStepIndex(activeStep);
    if (activeStep === ONBOARDING_STEP_IDS.SUCCESS) {
      goToStep(ONBOARDING_STEP_IDS.ACTIVATION);
      return;
    }
    const previous = ONBOARDING_STEPS[Math.max(0, currentIndex - 1)]?.id || ONBOARDING_STEP_IDS.MODE;
    goToStep(previous);
  };

  const goToResumePoint = () => {
    const next = getNextIncompleteStep(onboardingState);
    goToStep(next === ONBOARDING_STEP_IDS.SUCCESS ? ONBOARDING_STEP_IDS.SUCCESS : next);
  };

  const handleModeSelect = (mode) => {
    const nextProgress = persistProgress({
      setupMode: mode,
      setupModeSelectedAt: new Date().toISOString(),
    });
    trackHostOnboardingSetupModeSelected({ hostId: hostInfo?._id, mode });
    goToNextFromState(deriveStateWithProgress(nextProgress));
  };

  const handleStripeConnect = async () => {
    setStripeLoading(true);
    setStripeError("");
    try {
      const { data } = await request.get("/stripe/get-onboarding-link");
      const url = String(data.url || "");
      if (!url.startsWith("https://")) {
        setStripeError("Received an invalid Stripe URL. Please try again.");
        setStripeLoading(false);
        return;
      }
      window.location.href = url;
    } catch (error) {
      setStripeError(error.response?.data?.msg || "Could not open Stripe onboarding.");
      setStripeLoading(false);
    }
  };

  const handleStripeSkip = () => {
    const skippedAt = new Date().toISOString();
    trackHostOnboardingStripeSkipped({ hostId: hostInfo?._id, source });
    persistAndAdvance({ stripeSkipped: true, stripeSkippedAt: skippedAt });
  };

  const handlePmsDecision = (decision) => {
    const now = new Date().toISOString();
    if (decision === "no") {
      const nextProgress = persistProgress({
        pmsDecision: "no",
        pmsProvider: "none",
        pmsDecisionAt: now,
      });
      trackHostOnboardingPmsSelected({ hostId: hostInfo?._id, provider: "none" });
      goToNextFromState(deriveStateWithProgress(nextProgress));
      return;
    }

    persistProgress({ pmsDecision: "yes", pmsDecisionAt: now });
    trackHostOnboardingPmsSelected({ hostId: hostInfo?._id, provider: onboardingState.selectedPms || "pending" });
    goToStep(ONBOARDING_STEP_IDS.PMS_PROVIDER);
  };

  const handleProviderSelect = (provider) => {
    const nextProgress = persistProgress({
      pmsDecision: "yes",
      pmsProvider: provider,
      pmsDecisionAt: new Date().toISOString(),
    });
    trackHostOnboardingProviderSelected({ hostId: hostInfo?._id, provider });
    trackHostOnboardingPmsSelected({ hostId: hostInfo?._id, provider });

    if (provider === "other") {
      goToNextFromState(deriveStateWithProgress(nextProgress));
    }
  };

  const handleManualFallback = () => {
    const nextProgress = persistProgress({
      pmsDecision: "no",
      pmsProvider: "none",
      pmsManualFallbackAt: new Date().toISOString(),
    });
    goToNextFromState(deriveStateWithProgress(nextProgress));
  };

  const handlePropertyBasicsContinue = () => {
    trackHostOnboardingPropertyBasicsCompleted({ hostId: hostInfo?._id });
    goToStep(ONBOARDING_STEP_IDS.PROPERTY_ACCESS);
  };

  const handlePropertySave = async () => {
    if (!hasRequiredBasics(propertyForm)) {
      setPropertyError("Complete the property basics before saving.");
      goToStep(ONBOARDING_STEP_IDS.PROPERTY_BASICS);
      return;
    }
    if (!hasRequiredAccess(propertyForm)) {
      setPropertyError("Add detailed entry instructions before continuing.");
      return;
    }

    setPropertySaving(true);
    setPropertyError("");

    const payload = {
      checkInTime: propertyForm.checkInTime,
      checkOutTime: propertyForm.checkOutTime,
      listingTitle: propertyForm.listingTitle,
      description: propertyForm.description,
      keyInfo: propertyForm.keyInfo,
      address: propertyForm.address,
      hideAddress: propertyForm.hideAddress,
    };

    try {
      const existingPropertyId = onboardingState.firstActiveProperty?._id;
      const result = existingPropertyId
        ? await dispatch(updateHostProperty({ pid: existingPropertyId, propertyCredentials: payload }))
        : await dispatch(addProperty(payload));

      if (result.meta.requestStatus === "fulfilled") {
        if (!existingPropertyId) {
          trackHostOnboardingPropertyCreated({ hostId: hostInfo?._id });
        }
        trackHostOnboardingOperationalDetailsCompleted({ hostId: hostInfo?._id, propertyId: existingPropertyId });
        toast.success(existingPropertyId ? "Property access saved." : "Property created.");
        persistProgress({ propertyOperationsSavedAt: new Date().toISOString() });
        await dispatch(getHostProperties(hostInfo._id));
        goToStep(ONBOARDING_STEP_IDS.NOTIFICATIONS);
      } else {
        setPropertyError(result.payload?.error || "Could not save property.");
      }
    } catch (error) {
      setPropertyError(error?.response?.data?.error || "Could not save property.");
    } finally {
      setPropertySaving(false);
    }
  };

  const handleNotificationConfigured = () => {
    persistProgress({
      notificationPreferenceConfigured: true,
      notificationPreferenceConfiguredAt: new Date().toISOString(),
    });
  };

  const handleNotificationSkip = () => {
    persistAndAdvance({
      notificationPreferenceConfigured: true,
      notificationPreferenceSkippedAt: new Date().toISOString(),
    });
    toast.success("Delivery notification preference acknowledged.");
  };

  const handleNotificationContinue = () => {
    goToNextFromState(onboardingState);
  };

  const handlePropertySkip = () => {
    persistProgress({ propertySkippedAt: new Date().toISOString() });
    goToStep(ONBOARDING_STEP_IDS.MESSAGING);
  };

  const handleMessagingSkip = () => {
    persistProgress({
      messagingSkippedAt: new Date().toISOString(),
    });
    goToStep(ONBOARDING_STEP_IDS.ACTIVATION);
  };

  const handleActivate = () => {
    persistProgress({
      activationCompleted: true,
      activationCompletedAt: new Date().toISOString(),
    });
    trackHostOnboardingActivationCompleted({ hostId: hostInfo?._id, provider: onboardingState.selectedPms });
    goToStep(ONBOARDING_STEP_IDS.SUCCESS);
  };

  const loading = !progressLoaded || loadingHostDetails || loadingProperties;

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="text-center">
          <CircularProgress size={36} sx={{ color: "#1a1a1a" }} />
          <p className="mt-3 text-sm font-medium text-[#999999]">Preparing setup...</p>
        </div>
      </div>
    );
  }

  const onboardingMode = getOnboardingMode(onboardingState);
  const queryStep = searchParams.get("step");

  if (onboardingMode === "stripe-only" && !queryStep) {
    return (
      <>
        <div className="relative min-h-full bg-white">
          <div className="relative mx-auto flex max-w-4xl flex-col px-4 py-6 md:px-6">
            <StripeStep
              complete={onboardingState.completion.stripe}
              skipped={onboardingState.stripeSkipped}
              loading={stripeLoading}
              error={stripeError}
              onConnect={handleStripeConnect}
              onSkip={() => {
                handleStripeSkip();
                window.location.href = "/host";
              }}
              onContinue={() => { window.location.href = "/host"; }}
            />
          </div>
        </div>
        <ToastContainer />
      </>
    );
  }

  if (onboardingMode === "refresher" && !queryStep) {
    return (
      <>
        <RefresherSummary
          state={onboardingState}
          onGoToStep={(stepId) => {
            setStepInUrl(stepId);
            setActiveStep(stepId);
          }}
        />
        <ToastContainer />
      </>
    );
  }

  let renderedStep = null;
  if (activeStep === ONBOARDING_STEP_IDS.MODE) {
    renderedStep = <ModeStep setupMode={onboardingState.setupMode} onSelect={handleModeSelect} />;
  } else if (activeStep === ONBOARDING_STEP_IDS.STRIPE) {
    renderedStep = (
      <StripeStep
        complete={onboardingState.completion.stripe}
        skipped={onboardingState.stripeSkipped}
        loading={stripeLoading}
        error={stripeError}
        onConnect={handleStripeConnect}
        onSkip={handleStripeSkip}
        onContinue={goToResumePoint}
      />
    );
  } else if (activeStep === ONBOARDING_STEP_IDS.PMS) {
    renderedStep = <PmsPathStep pmsDecision={onboardingState.pmsDecision} onDecision={handlePmsDecision} />;
  } else if (activeStep === ONBOARDING_STEP_IDS.PMS_PROVIDER) {
    renderedStep = (
      <PmsProviderStep
        state={onboardingState}
        hostInfo={hostInfo}
        onProviderSelect={handleProviderSelect}
        onProviderConnected={(provider) => {
          trackHostOnboardingPmsConnected({ hostId: hostInfo?._id, provider });
          dispatch(getHostProfile(hostInfo._id));
        }}
        onManualFallback={handleManualFallback}
        onContinue={goToResumePoint}
      />
    );
  } else if (activeStep === ONBOARDING_STEP_IDS.PROPERTY_BASICS) {
    renderedStep = (
      <PropertyBasicsStep
        state={onboardingState}
        form={propertyForm}
        setForm={setPropertyForm}
        onContinue={handlePropertyBasicsContinue}
        onSkip={handlePropertySkip}
      />
    );
  } else if (activeStep === ONBOARDING_STEP_IDS.PROPERTY_ACCESS) {
    renderedStep = (
      <PropertyAccessStep
        state={onboardingState}
        form={propertyForm}
        setForm={setPropertyForm}
        saving={propertySaving}
        error={propertyError}
        onSave={handlePropertySave}
        onSkip={handlePropertySkip}
      />
    );
  } else if (activeStep === ONBOARDING_STEP_IDS.NOTIFICATIONS) {
    renderedStep = (
      <NotificationsStep
        hostId={hostInfo?._id}
        state={onboardingState}
        onConfigured={handleNotificationConfigured}
        onSkip={handleNotificationSkip}
        onContinue={handleNotificationContinue}
      />
    );
  } else if (activeStep === ONBOARDING_STEP_IDS.MESSAGING) {
    renderedStep = (
      <MessagingStep
        state={onboardingState}
        hostId={hostInfo?._id}
        onContinue={() => goToStep(ONBOARDING_STEP_IDS.ACTIVATION)}
        onSkip={handleMessagingSkip}
      />
    );
  } else if (activeStep === ONBOARDING_STEP_IDS.SUCCESS) {
    renderedStep = <SuccessStep state={onboardingState} onGoToStep={goToStep} />;
  } else {
    renderedStep = <ActivationStep state={onboardingState} onActivate={handleActivate} onGoToStep={goToStep} />;
  }

  return (
    <>
      <OnboardingStage
        state={onboardingState}
        activeStep={activeStep}
        direction={direction}
        onBack={goToPreviousStep}
        source={source}
      >
        {renderedStep}
      </OnboardingStage>
      <ToastContainer />
    </>
  );
}

export default HostOnboardingScreen;
